<!-- eslint-disable vue-a11y/click-events-have-key-events -->
<template>
  <div
    class="sam-stepper"
    data-samora
  >
    <ul class="sam-stepper__items-container">
      <template v-for="(step, index) in steps">
        <li
          :key="`stepper-item-${index}`"
          :class="[
            'sam-stepper__item',
            { 'is-active': index === currentStep },
            { 'is-skipped': step.skipped },
          ]"
          @click="emitClick(step, index)"
        >
          <div class="step-number">
            <SamTooltip
              v-if="step.tooltip"
              :message="step.tooltip"
              :anchor-name="`step-icon-${index}`"
            />
            <SamIcon
              v-if="index < currentStep"
              :id="`step-icon-${index}`"
              class="step-completed-icon"
              :icon="getIcon(step)"
              filled
              :size="10"
            />
            <span v-else>{{ index + 1 }}</span>
          </div>
          <div>
            <p class="step-label">
              {{ step.label }}
            </p>
            <p
              v-if="index < currentStep && ($slots['item-sub-label'] || step.caption)"
              class="step-caption"
            >
              <template v-if="$slots['item-sub-label']">
                <slot
                  name="item-sub-label"
                  :step="step"
                  :index="index"
                />
              </template>
              <template v-else-if="step.caption">
                {{ step.caption }}
              </template>
            </p>
          </div>
        </li>
        <hr
          v-if="index < steps.length - 1"
          :key="`stepper-separator-${index}`"
          :class="['sam-stepper__item-separator', { 'is-active': index < currentStep }]"
          role="separator"
        />
      </template>
    </ul>
  </div>
</template>

<script>
import { objectHasOwnProperties } from '../../../utils/utils';
import SamIcon from '../SamIcon/SamIcon.vue';

export default {
  name: 'SamStepper',
  components: { SamIcon },
  props: {
    /**
     * The stepper data array
     * @values label, caption (for object structure)
     */
    steps: {
      type: Array,
      required: true,
      default: () => [],
      validator: (val) => val.every?.((stepObj) => objectHasOwnProperties(stepObj, 'label')),
    },
    /**
     * The stepper current step
     */
    currentStep: {
      type: Number,
      required: true,
    },
  },
  methods: {
    getIcon(step) {
      return step.skipped ? 'arrow-button-line-right' : 'interface-confirm';
    },
    emitClick(step, index) {
      this.$emit('click:step', { ...step, index });
    },
  },
};
</script>

<style lang="scss" scoped>
.sam-stepper {
  @extend .pb-6;

  display: block;
  box-sizing: border-box;

  .sam-stepper__items-container {
    @extend .d-flex, .wrap-flex;
    row-gap: $size--2;

    .sam-stepper__item {
      @extend .px-2;

      &:first-child {
        @extend .pl-0;
      }

      &:last-child {
        @extend .pr-0;
      }

      display: grid;
      grid-column-gap: $size--2;
      grid-template-columns: auto auto;

      .step-label {
        @extend .text--body-2;
        color: $primary--black-rock;
      }

      .step-caption {
        @extend .text--caption;
        color: $neutral--80;
      }

      .step-number {
        @extend .text--caption, .d-flex, .items-center, .center-xs;
        font-size: 10px !important;
        text-align: center;
        color: $pure--white;
        background-color: $accent--unbabel-blue;
        width: $size--5;
        height: $size--5;
        border-radius: 100%;
      }

      &.is-active {
        .step-label {
          @extend .text--subtitle;
        }

        ~ .sam-stepper__item {
          .step-label {
            color: $neutral--80;
          }

          .step-number {
            @extend .text--caption;
            background-color: $neutral--80;
          }
        }
      }

      &.is-skipped {
        .step-number {
          background-color: $neutral--80;
        }

        .step-label {
          color: $neutral--80;
          text-decoration: line-through;
        }
      }
    }

    .sam-stepper__item-separator {
      margin-top: $size--2-half;
      flex-grow: 1;
      min-width: $size--8;
      height: 0;
      border: $size--0--fourth solid $neutral--60;

      &.is-active {
        border-color: $primary--black-rock;
      }
    }
  }
}
</style>
